import { useLingui } from "@lingui/react/macro";
import type { Bot, IntegrationSetupState } from "@rakazo/contracts";
import { Button, NativeSelect, NativeSelectOption } from "@rakazo/ui-web";
import { ArrowLeft } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IntegrationSetup } from "../components/integrations/IntegrationSetup";
import { rpc } from "../lib/rpc";

export function IntegrationSetupPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const serverSetup = params.get("mode") !== "mcp";
  const returnToSettings = params.get("from") === "settings";
  const returnPath = returnToSettings ? "/app?settings=connections" : "/app";
  const { t } = useLingui();
  const assistantId = useId();
  const [bots, setBots] = useState<Bot[]>([]);
  const [botId, setBotId] = useState("");
  const [ready, setReady] = useState(false);
  const [setupState, setSetupState] = useState<IntegrationSetupState | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setReady(false);
    setError(false);
    let cancelled = false;
    void Promise.all([rpc.bots.list(), serverSetup ? rpc.integrationSetup.get() : null])
      .then(([rows, setup]) => {
        if (cancelled) return;
        if (serverSetup && !setup?.canConfigure) {
          navigate("/app", { replace: true });
          return;
        }
        if (!rows.length) {
          navigate("/onboarding", { replace: true });
          return;
        }
        setSetupState(setup);
        setBots(rows);
        setBotId(rows[0]?.id ?? "");
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [serverSetup, navigate, attempt]);
  return (
    <main className="min-h-dvh bg-background px-4 py-6 sm:px-8 sm:py-10">
      <div className="mx-auto max-w-5xl">
        <Button
          variant="ghost"
          className="mb-7 min-h-11 gap-2 rounded-full"
          disabled={busy}
          onClick={() => navigate(returnPath)}
        >
          <ArrowLeft className="size-4 rtl:rotate-180" />
          {returnToSettings ? t`Back to settings` : t`Back to chat`}
        </Button>
        {ready ? (
          <IntegrationSetup
            key={serverSetup ? "server" : "mcp"}
            standalone
            serverSetup={serverSetup}
            initialState={setupState}
            botId={botId || undefined}
            onBusyChange={setBusy}
            assistantPicker={(disabled) => (
              <label htmlFor={assistantId} className="block text-sm">
                <span className="mb-2 block font-medium">{t`Assistant`}</span>
                <NativeSelect
                  id={assistantId}
                  aria-label={t`Bot`}
                  value={botId}
                  disabled={disabled}
                  onChange={(event) => setBotId(event.target.value)}
                  className="w-full"
                >
                  {bots.map((bot) => (
                    <NativeSelectOption key={bot.id} value={bot.id}>
                      {bot.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </label>
            )}
            onDone={() => navigate(bots.length ? returnPath : "/onboarding")}
          />
        ) : (
          <div className="rounded-2xl border border-border/60 bg-card p-6">
            <p role={error ? "alert" : "status"}>
              {error ? t`Could not load integration settings.` : t`Loading integrations…`}
            </p>
            {error ? (
              <Button
                variant="outline"
                className="mt-4 min-h-11 rounded-full"
                onClick={() => setAttempt((current) => current + 1)}
              >{t`Retry`}</Button>
            ) : null}
          </div>
        )}
      </div>
    </main>
  );
}
